import React, { useState, useEffect, useCallback } from 'react'; // Ajoutez useCallback
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import { getPaginatedOrderValidators, deleteValidator } from '../../service/api/validatorService';
import { toast } from 'react-toastify';
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import NewValidatorModal from './NewValidatorModal';
import ConfirmationModal from './ConfirmationModal';
import { FaEdit, FaTrash } from 'react-icons/fa';


function ValidateurOrdres() {
  const [showValidatorFormModal, setShowValidatorFormModal] = useState(false); // Renommé
  const [validatorToEdit, setValidatorToEdit] = useState(null); // Nouveau state pour l'édition
  const [validators, setValidators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 5;

  const selectedDomain = useSelectedDomain();
  const domainId = selectedDomain?.id;

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [validatorToDelete, setValidatorToDelete] = useState(null);

  // --- Récupération des validateurs d'ordres ---
  // Utilisez useCallback pour mémoriser la fonction fetchValidators
  const fetchValidators = useCallback(async (currentPage = page) => {
    if (!domainId) {
      setLoading(false);
      setValidators([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getPaginatedOrderValidators(domainId, currentPage, pageSize);
      setValidators(data.items || []);
      setTotalPages(data.totalPages || 1);
      setPage(data.pageNumber || 1);
    } catch (err) {
      setError(err.message || "Impossible de récupérer les validateurs d'ordre.");
      setValidators([]);
    } finally {
      setLoading(false);
    }
  }, [domainId, page, pageSize]); // Dépendances pour useCallback

  useEffect(() => {
    fetchValidators();
  }, [domainId, page, fetchValidators]); // Ajoutez fetchValidators aux dépendances de useEffect

  // --- Gestionnaires de modale pour l'ajout/modification ---
  const handleOpenNewValidatorModal = () => {
    if (!domainId) {
      toast.warn("Veuillez d'abord sélectionner un domaine.");
      return;
    }
    setValidatorToEdit(null); // S'assurer que c'est le mode création
    setShowValidatorFormModal(true);
  };

  const handleOpenEditValidatorModal = (validator) => {
    setValidatorToEdit(validator); // Passer le validateur à modifier
    setShowValidatorFormModal(true);
  };

  const handleCloseValidatorFormModal = () => {
    setShowValidatorFormModal(false);
    setValidatorToEdit(null); // Réinitialiser le validateur à modifier à la fermeture
  };

  const handleValidatorOperationSuccess = () => {
    toast.success("Opération réussie ! La liste est mise à jour.");
    fetchValidators(page); // Rafraîchit la liste en restant sur la page actuelle
  };

  // --- Gestionnaires pour la suppression (inchangés) ---
  const handleOpenDeleteConfirmation = (validator) => {
    setValidatorToDelete(validator);
    setShowDeleteConfirmationModal(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setShowDeleteConfirmationModal(false);
    setValidatorToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (validatorToDelete) {
      setLoading(true);
      try {
        await deleteValidator(validatorToDelete.id);
        toast.success(`Validateur "${validatorToDelete.accountName || validatorToDelete.firstName + ' ' + validatorToDelete.lastName}" supprimé avec succès !`);
        fetchValidators(page);
      } catch (err) {
        toast.error(`Erreur lors de la suppression du validateur: ${err.message || "Une erreur est survenue."}`);
      } finally {
        setLoading(false);
        handleCloseDeleteConfirmation();
      }
    }
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row mb-2 gx-3 gy-2 align-items-center">
          <div className="col-xl-12">
            <div className="page-titles d-flex justify-content-between align-items-center">
              <nav style={{ "--bs-breadcrumb-divider": "'>'" }} aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item fw-bold text-primary text-uppercase m-0" style={{ letterSpacing: "1px", textShadow: "1px 1px 2px rgba(0, 0, 0, 0.2)" }}>Validateurs</li>
                </ol>
              </nav>
              <div className="d-flex gap-2">
                <button className="btn btn-primary" onClick={handleOpenNewValidatorModal}>
                  Nouveau Validateur
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-end mb-4 p-3 bg-white shadow-sm rounded">
        <h4 className="fw-bold text-primary text-uppercase mb-3 m-0 text-center fs-4" style={{ letterSpacing: "1px", textShadow: "1px 1px 2px rgba(0, 0, 0, 0.2) " }}>
          Validateurs d'Ordres
        </h4>
      </div>

      <div className="card lastest_trans">
        <div className="card-body px-3 py-0">
          <div className="table-responsive">
            <table className="table trans-table table-striped table-hover border-no tbl-btn short-one">
              <thead className="text-center fw-bold fs-1">
                <tr>
                  <th className="fw-bold fs-4">Nom Validateur</th>
                  <th className="fw-bold fs-4">Nom Domaine</th>
                  <th className="fw-bold fs-4">Type de Validation</th>
                  <th className="fw-bold fs-4">Actions</th>
                </tr>
              </thead>
              <tbody className="text-center fw-bold">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      <Spinner animation="border" variant="primary" />
                      <p>Chargement des validateurs...</p>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="4" className="text-danger text-center">Erreur: {error}</td>
                  </tr>
                ) : validators.length > 0 ? (
                  validators.map((validator) => (
                    <tr key={validator.id}>
                      <td>{validator.accountName || `${validator.firstName} ${validator.lastName}`}</td>
                      <td>{validator.domainName || selectedDomain?.name}</td>
                      <td>Ordre</td>
                      <td>
                        <div>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                          <button
                              className="btn btn-primary shadow btn-xs sharp me-1"
                              onClick={(e) => { e.preventDefault(); handleOpenEditValidatorModal(validator); }}
                          >
                              <FaEdit />
                          </button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                            <button onClick={(e) => { e.preventDefault(); handleOpenDeleteConfirmation(validator); }} className="btn btn-danger shadow btn-xs sharp">
                              <FaTrash />
                            </button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">Aucun validateur d'Ordre trouvé pour ce domaine.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Contrôles de pagination */}
          {totalPages > 1 && (
            <nav className="mt-3">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setPage(prev => Math.max(prev - 1, 1))}>
                    <i className="fa-solid fa-angle-left"></i>
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => (
                  <li key={i} className={`page-item ${page === i + 1 ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setPage(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}>
                    <i className="fa-solid fa-angle-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>

      {/* Modale de formulaire (création/modification) */}
      <NewValidatorModal
        show={showValidatorFormModal}
        onClose={handleCloseValidatorFormModal}
        onValidatorCreated={handleValidatorOperationSuccess} // Utilisez le même callback pour les deux opérations
        validatorToEdit={validatorToEdit} // Passez le validateur à modifier (ou null pour la création)
      />

      {/* Modale de confirmation de suppression */}
      <ConfirmationModal
        show={showDeleteConfirmationModal}
        onClose={handleCloseDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        title="Confirmer la suppression du validateur"
        message={
          validatorToDelete ? (
            <>
              Êtes-vous sûr de vouloir supprimer le validateur&nbsp;
              <strong className="text-uppercase fs-4 p-1 text-dark">
                {validatorToDelete.accountName
                  ? validatorToDelete.accountName
                  : `${validatorToDelete.firstName} ${validatorToDelete.lastName}`}
              </strong>
              &nbsp;?
            </>
          ) : (
            "Êtes-vous sûr de vouloir supprimer ce validateur ?"
          )
        }
      />
    </>
  );
}

export default ValidateurOrdres;